import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createRoot } from 'react-dom/client';
import axios from 'axios';
import './styles.css';
import { applyStyleVars } from './utils/styleVars';
import { useRecaptcha } from './hooks/useRecaptcha';
import RecaptchaV2 from './components/RecaptchaV2';
import { getWaitlistFrontData } from './frontData';
import { loadRecaptcha } from './utils/recaptchaLoader';

type WaitlistGlobals = {
  ajax_url: string;
  actions: { submit?: string };
  nonce: { submit?: string };
  i18n?: {
    formMessage?: string;
    placeholderEmail?: string;
    buttonAdd?: string;
    buttonLeave?: string;
    subscribeText?: string;
    errorText?: string;
    alreadyText?: string;
    confirmText?: string;
  };
  flags?: {
    showCounter?: boolean;
    showLeave?: boolean;
    doubleOptIn?: boolean;
    policyRequired?: boolean;
  };
  policy?: {
    enabled?: boolean;
    text?: string;
    errorText?: string;
  };
  recaptcha?: {
    enabled?: boolean;
    version?: 'v2' | 'v3';
    publicKey?: string;
    errorText?: string;
  };
  styles?: any;
};

type AjaxResponse = {
  type?: 'success' | 'error';
  msg?: string;
};

const getGlobals = (): WaitlistGlobals => {
  return getWaitlistFrontData<WaitlistGlobals>();
};

const toForm = (data: Record<string, any>) => {
  const form = new URLSearchParams();
  Object.keys(data).forEach((key) => {
    const val = (data as any)[key];
    if (val === undefined || val === null) return;
    form.append(key, String(val));
  });
  return form;
};

const WaitlistWidget: React.FC<{ root: HTMLElement }> = ({ root }) => {
  const globals = useMemo(getGlobals, []);
  const recaptcha = useRecaptcha(globals.recaptcha);
  useEffect(() => {
    if (globals.recaptcha?.enabled && globals.recaptcha.version === 'v3' && globals.recaptcha.publicKey) {
      loadRecaptcha('v3', globals.recaptcha.publicKey).catch(() => {});
    }
  }, [globals.recaptcha?.enabled, globals.recaptcha?.version, globals.recaptcha?.publicKey]);
  const productId = root.dataset.productId || '';
  const userId = root.dataset.userId || '';
  const initialEmail = root.dataset.userEmail || '';
  const initialSubscribed = root.dataset.isSubscribed === '1';
  const [email, setEmail] = useState(initialEmail);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(initialSubscribed ? globals.i18n?.subscribeText || null : null);
  const [messageType, setMessageType] = useState<'success' | 'error' | null>(initialSubscribed ? 'success' : null);
  const [subscribed, setSubscribed] = useState(initialSubscribed);
  const [showLeave, setShowLeave] = useState(initialSubscribed);
  const [policyAccepted, setPolicyAccepted] = useState(!globals.policy?.enabled || !globals.flags?.policyRequired);

  const submit = useCallback(
    async (mode: 'register' | 'leave') => {
      if (!productId) {
        setMessage('Missing product id.');
        setMessageType('error');
        return;
      }
      if (mode === 'register' && !email) {
        setMessage(globals.i18n?.errorText || 'Please enter your email.');
        setMessageType('error');
        return;
      }
      setLoading(true);
      setMessage(null);
      setMessageType(null);
      try {
        const action = globals.actions?.submit || 'ips_wooorderwaitlist_submit';
        const nonce = globals.nonce?.submit || root.dataset.waitlistNonce || '';

        let gtoken = '';
        if (globals.recaptcha?.enabled && globals.recaptcha?.publicKey) {
          const recaptchaError = globals.recaptcha.errorText || globals.i18n?.errorText || 'Captcha verification failed.';
          const recaptchaId =
            globals.recaptcha.version === 'v2' ? 'ips-waitlist-recaptcha' : undefined;
          try {
            if (recaptchaId) {
              await recaptcha.ensureRendered(recaptchaId);
            }
            gtoken = await recaptcha.getToken(recaptchaId as any);
          } catch (err) {
            setMessage(recaptchaError);
            setMessageType('error');
            setLoading(false);
            return;
          }
          if (!gtoken) {
            setMessage(recaptchaError);
            setMessageType('error');
            setLoading(false);
            return;
          }
        }

        const payload: Record<string, any> = {
          action,
          context: 'frontend',
          _ips_wooorderwaitlist_product_id: productId,
          '_ips_wooorderwaitlist_users_list-action': mode,
          'ips-wooorderwaitlist-user-email': email,
          nonce,
        };
        if (userId) {
          payload['_ips_wooorderwaitlist_user_id'] = userId;
        }
        if (globals.policy?.enabled && globals.flags?.policyRequired) {
          payload['ips-wooorderwaitlist-policy-check'] = policyAccepted ? 'yes' : 'no';
        }
        if (gtoken) {
          payload['gtoken'] = gtoken;
        }

        const { data } = await axios.post<AjaxResponse>(globals.ajax_url || '/wp-admin/admin-ajax.php', toForm(payload));
        const resType = (data as any)?.type as 'success' | 'error' | undefined;
        const resMsg = (data as any)?.msg as string | undefined;
        if (resType === 'error') {
          setMessage(resMsg || globals.i18n?.errorText || 'Request failed.');
          setMessageType('error');
        } else {
          const subscribedNow = mode === 'register';
          setSubscribed(subscribedNow);
          setMessage(resMsg || globals.i18n?.subscribeText || 'You have been added to the waitlist.');
          setMessageType('success');
          setShowLeave(initialSubscribed); // Leave-Button nur, wenn Status beim Laden schon subscribed war
        }
      } catch (err: any) {
        setMessage(globals.i18n?.errorText || 'Request failed.');
        setMessageType('error');
      } finally {
        setLoading(false);
      }
    },
    [globals, productId, userId, email, policyAccepted, root.dataset.waitlistNonce, initialSubscribed, recaptcha]
  );

  const onSubmit = useCallback(
    (ev: React.FormEvent) => {
      ev.preventDefault();
      if (globals.policy?.enabled && globals.flags?.policyRequired && !policyAccepted) {
        setMessage(globals.policy?.errorText || globals.policy?.text || 'Please accept the Privacy Policy.');
        setMessageType('error');
        return;
      }
      submit(subscribed ? 'leave' : 'register');
    },
    [submit, subscribed, policyAccepted, globals]
  );

  const btnLabel = subscribed
    ? globals.i18n?.buttonLeave || 'Leave waitlist'
    : globals.i18n?.buttonAdd || 'Add to waitlist';

  return (
    <div className="ips-waitlist-widget">
      {!subscribed ? (
        <>
          {globals.i18n?.formMessage ? (
            <div
              className="ips-waitlist-message"
              dangerouslySetInnerHTML={{ __html: globals.i18n.formMessage }}
            />
          ) : null}
          <form onSubmit={onSubmit} className="ips-waitlist-form">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={globals.i18n?.placeholderEmail || 'Enter your email address'}
              aria-label={globals.i18n?.placeholderEmail || 'Email'}
              required
              disabled={loading}
            />
            <button type="submit" disabled={loading}>
              {loading ? '…' : btnLabel}
            </button>
            {globals.policy?.enabled && globals.flags?.policyRequired && (
              <label className="ips-waitlist-policy">
                <input
                  type="checkbox"
                  name="ips-wooorderwaitlist-policy-check"
                  checked={policyAccepted}
                  onChange={(e) => setPolicyAccepted(e.target.checked)}
                  disabled={loading}
                  value="yes"
                />
                <span dangerouslySetInnerHTML={{ __html: globals.policy?.text || '' }} />
              </label>
            )}
          </form>
          {globals.recaptcha?.enabled && globals.recaptcha.version === 'v2' && globals.recaptcha.publicKey ? (
            <RecaptchaV2 ensureRendered={recaptcha.ensureRendered} containerId="ips-waitlist-recaptcha" />
          ) : null}
          {message ? (
            <div className={`ips-waitlist-notice ips-waitlist-notice--${messageType || 'info'}`}>
              <span dangerouslySetInnerHTML={{ __html: message }} />
            </div>
          ) : null}
        </>
      ) : (
        <div className="ips-waitlist-subscribed">
          <div className="ips-waitlist-notice ips-waitlist-notice--success">
            <span
              dangerouslySetInnerHTML={{
                __html: message || globals.i18n?.subscribeText || '',
              }}
            />
          </div>
          {showLeave && globals.flags?.showLeave !== false && (
            <button className="ips-waitlist-leave" onClick={() => submit('leave')} disabled={loading}>
              {globals.i18n?.buttonLeave || 'Leave waitlist'}
            </button>
          )}
          {globals.recaptcha?.enabled && globals.recaptcha.version === 'v2' && globals.recaptcha.publicKey ? (
            <RecaptchaV2 ensureRendered={recaptcha.ensureRendered} containerId="ips-waitlist-recaptcha" />
          ) : null}
        </div>
      )}
    </div>
  );
};

const mount = () => {
  const nodes = document.querySelectorAll<HTMLElement>('.ips-waitlist-root:not([data-ips-react-ready])');
  nodes.forEach((root) => {
    applyStyleVars(root, getWaitlistFrontData<WaitlistGlobals>().styles);
    createRoot(root).render(<WaitlistWidget root={root} />);
    root.setAttribute('data-ips-react-ready', '1');
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', mount);
} else {
  mount();
}

const observer = new MutationObserver((mutations) => {
  for (const m of mutations) {
    if (m.type !== 'childList') continue;
    if ([...m.addedNodes].some((n) => n instanceof Element && n.querySelector?.('.ips-waitlist-root'))) {
      mount();
      break;
    }
  }
});

observer.observe(document.body, { childList: true, subtree: true });

export {};
